import React from 'react';
import { useBooleanFlagValue } from '@worktango/feature-flags-react';
import Button from '~/components/Button';
import { WorkTangoCoachPopOut } from './WorkTangoCoachPopOut';
import { icons } from '~/tools/icons';
import { useAppSelector } from '~/behaviors/redux';
import { permissionSelectors } from '~/behaviors/selectors/permission.selectors';
import type { Permissions } from '~/behaviors/selectors/role.selectors';
import { accountSelectors } from '~/behaviors/selectors/account.selectors';
import type { AccountReducer } from '~/behaviors/types/account.types';
import { useLocation } from 'react-router-dom';

import { jss } from '~/tools/jss';
import ReactDOM from 'react-dom';
import { graphqlRequest } from '~/adapters/graphqlRequest';
import { CreateSurveySummaryConversationDocument } from '~/gql/graphql';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const useStyles = jss.makeStyles((theme) => ({
  wrapper: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    right: 0,
    zIndex: 1001,
    left: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    pointerEvents: 'none',
  },
  visibleWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    pointerEvents: 'auto',
  },
  hidden: {
    opacity: 0,
    zIndex: -1,
  },
}));

export const WorkTangoCoach: React.FC = () => {
  const canSeeAiAssistant = useBooleanFlagValue('ai-assistant-badge', false);
  const userPermissions = useAppSelector<Permissions | null>(permissionSelectors.selectPermissions());
  const account = useAppSelector<AccountReducer | null>(accountSelectors.selectAccount());

  const isAdmin = account?.isAdmin || false;
  const hasAccessPermission = userPermissions?.canAccessAiAssistant || false;

  const shouldShowAiAssistant = canSeeAiAssistant && (hasAccessPermission || isAdmin);

  const [isOpen, setIsOpen] = React.useState(false);
  const classes = useStyles();
  const flyoutRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (flyoutRef.current && !flyoutRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  const query = useQuery();
  const coachingSummary = query.get('coachingSummary');
  const surveys = query.get('surveys');

  // Auto-open coach with pre-created conversation when coachingSummary parameter is present
  const [preCreatedConversationId, setPreCreatedConversationId] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (coachingSummary === 'true' && surveys && shouldShowAiAssistant) {
      // Parse surveys parameter (could be a single ID or comma-separated IDs)
      const surveyIds = surveys
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      const singleSurveyProvided = surveyIds.length === 1;
      if (singleSurveyProvided) {
        // We only support coaching summaries for a single survey at a time!
        const surveyId = parseInt(surveyIds[0]);
        if (!isNaN(surveyId)) {
          graphqlRequest<any>(CreateSurveySummaryConversationDocument, {
            surveyId: surveyId,
          })
            .then((response) => {
              setPreCreatedConversationId(response.data.createSurveySummaryConversation.conversationId);
              setIsOpen(true);
            })
            .catch((error) => {
              // Fallback: still open coach but without pre-created conversation
              setIsOpen(true);
            });
        }
      }
    }
  }, [coachingSummary, surveys, shouldShowAiAssistant]);

  return (
    <>
      {shouldShowAiAssistant && (
        <Button startIcon={<icons.Star />} onClick={() => setIsOpen(true)}>
          WorkTango Coach
        </Button>
      )}
      <InPortal visible={isOpen && shouldShowAiAssistant}>
        <div className={classes.wrapper}>
          <div ref={flyoutRef} className={classes.visibleWrapper}>
            <WorkTangoCoachPopOut onClose={() => setIsOpen(false)} initialConversationId={preCreatedConversationId} />
          </div>
        </div>
      </InPortal>
    </>
  );
};

function InPortal({ children, visible }: { children: React.ReactNode; visible: boolean }): React.ReactPortal | null {
  const id = React.useId();
  const hasRendered = React.useRef(false);
  const classes = useStyles();

  const targetElement = React.useMemo(() => {
    const existingElement = document.getElementById(id);
    if (existingElement) {
      return existingElement as HTMLDivElement;
    }
    const newElement = document.createElement('div');
    newElement.id = id;
    document.body.appendChild(newElement);
    return newElement;
  }, [id]);

  if (!visible && !hasRendered.current) {
    return null;
  }

  if (visible && !hasRendered.current) {
    hasRendered.current = true;
  }

  return ReactDOM.createPortal(
    <div className={!visible ? classes.hidden : undefined}>{children}</div>,
    targetElement,
  ) as unknown as React.ReactPortal;
}
